import { Observable } from 'rxjs';

type EventBusSingleKey = string;
type EventBusGroupKey = `${string}:${string}`;

export type EventBusKey =
  | EventBusSingleKey
  | `${EventBusSingleKey}:${EventBusSingleKey}`
  | `${EventBusSingleKey}:${EventBusGroupKey}`;

/**
 * Defines a strong typing between a message key and a data type.
 * Used to ensure type safety when casting and listening to events.
 * @template T The type of data associated with the event.
 */
export type EventBusBinding<T> = {
  key: EventBusKey;
  data?: T;
};

/**
 * Defines the event action type which returns a binding from a message type.
 * @template T The type of data associated with the event.
 */
export type EventBusAction<T> = (data?: T) => EventBusBinding<T>;

// Creates a event action with a key
export function createEventAction<T>(key: string): EventBusAction<T> {
  return (data?: T) => {
    return { key: key, data: data };
  };
}

/**
 * Defines the event metadata type which contains the event binding and the event timestamp.
 * @template T The type of data associated with the event.
 */
export type MetaData<T> = {
  id: string;

  /**
   * Original key associated to the message.
   */
  key: EventBusKey;

  /**
   * Data associated to message. It's optional.
   */
  data: T | undefined;

  /**
   * Gets the time in milliseconds in which the message was generated.
   */
  timestamp: number;
};

/**
 * Abstract base class for the Event Bus system.
 * Provides the contract for casting and listening to strongly-typed events.
 */
export abstract class FzEventBus {
  /**
   * Casts (publishes) an event to the bus.
   *
   * @template T The type of the event payload.
   * @param {EventBusKey} key The event key.
   * @param {T} data The event data.
   */
  abstract cast<T extends object>(key: EventBusKey, data?: T): void;

  /**
   * Subscribes to an event on the bus.
   *
   * @template T The type of the event payload.
   * @param {EventBusKey} key The event key to listen for.
   * @returns {Observable<MetaData<T>>} An Observable that emits the event metadata (binding and timestamp) when the event occurs.
   */

  abstract on<T extends object>(key: EventBusKey): Observable<MetaData<T>>;
}
